import React, { useEffect, useRef, useCallback } from 'react';
import { driver } from 'driver.js';
import type { Driver } from 'driver.js';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';
import { useLocalStorage } from '@uidotdev/usehooks';
import { useAssistantChat, sidebarOpenAtom, calendarViewSwitcherAtom } from '../../data-access';

import 'driver.js/dist/driver.css';
import './walkthrough.css';

import { walkthroughChatPrefillAtom } from '../../data-access';
import { buildWalkthroughSteps, getWalkthroughAiPrefill } from './steps';
import { playGhostDrag, GHOST_ANIM_MS } from './ghost-drag';

const STORAGE_KEY = 'walkthrough_complete';
const MOBILE_INSTALL_DIALOG_OPENED_KEY = 'mobile_install_dialog_opened';
const MOBILE_BREAKPOINT = 768;
const GHOST_GAP_MS = 400;
const STEP3_TOTAL_MS = 400 + GHOST_ANIM_MS + GHOST_GAP_MS + GHOST_ANIM_MS + 600;

export function isWalkthroughComplete(): boolean {
  return localStorage.getItem(STORAGE_KEY) === 'true';
}

export function resetWalkthrough(): void {
  localStorage.removeItem(STORAGE_KEY);
}

function markWalkthroughComplete(): void {
  localStorage.setItem(STORAGE_KEY, 'true');
}

function isMobile(): boolean {
  return window.innerWidth < MOBILE_BREAKPOINT;
}

export const WalkthroughController: React.FC = () => {
  const { t } = useTranslation();
  const [shouldMobileInstallDialogOpen] = useLocalStorage(MOBILE_INSTALL_DIALOG_OPENED_KEY, false);
  const driverRef = useRef<Driver | null>(null);
  const ghostAutoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setChatPrefill = useSetRecoilState(walkthroughChatPrefillAtom);
  const setSidebarOpen = useSetRecoilState(sidebarOpenAtom);
  const setCalendarView = useSetRecoilState(calendarViewSwitcherAtom);
  const { setOpenAssistantChat } = useAssistantChat();

  const cleanup = useCallback(() => {
    if (ghostAutoAdvanceTimerRef.current) {
      clearTimeout(ghostAutoAdvanceTimerRef.current);
      ghostAutoAdvanceTimerRef.current = null;
    }
  }, []);

  const handleStepChange = useCallback(
    (stepIndex: number) => {
      cleanup();

      if (stepIndex === 2) {
        if (!isMobile()) {
          setTimeout(() => playGhostDrag(), 400);
          setTimeout(() => playGhostDrag(), 400 + GHOST_ANIM_MS + GHOST_GAP_MS);
        }

        // Open sidebar before auto-advancing to step 4
        setTimeout(() => {
          setSidebarOpen(true);
          setOpenAssistantChat(true);
        }, STEP3_TOTAL_MS - 600);

        const handleDragEnd = () => {
          document.removeEventListener('dragend', handleDragEnd);
          if (ghostAutoAdvanceTimerRef.current) {
            clearTimeout(ghostAutoAdvanceTimerRef.current);
            ghostAutoAdvanceTimerRef.current = null;
          }
          setTimeout(() => driverRef.current?.moveNext(), 600);
        };

        document.addEventListener('dragend', handleDragEnd);

        ghostAutoAdvanceTimerRef.current = setTimeout(() => {
          document.removeEventListener('dragend', handleDragEnd);
          driverRef.current?.moveNext();
        }, STEP3_TOTAL_MS);
      }

      if (stepIndex === 3) {
        setChatPrefill({ text: getWalkthroughAiPrefill(t), version: Date.now() });
        setTimeout(() => driverRef.current?.refresh(), 500);
      }
    },
    [cleanup, setChatPrefill, setSidebarOpen, setOpenAssistantChat, t]
  );

  const startWalkthrough = useCallback(() => {
    // Ensure step 1 always highlights the day-view calendar
    setCalendarView('day');

    const steps = buildWalkthroughSteps(t, { isMobile: isMobile() });

    const driverObj = driver({
      steps,
      animate: true,
      overlayColor: 'rgba(0, 0, 0, 0.45)',
      stagePadding: 8,
      allowClose: false,
      onPopoverRender: (popover, { state }) => {
        popover.previousButton.style.display = (state.activeIndex ?? 0) === 0 ? 'none' : '';
      },
      onHighlightStarted: (_element, _step, { state }) => {
        handleStepChange(state.activeIndex ?? 0);
      },
      onDestroyStarted: () => {
        cleanup();
        markWalkthroughComplete();
        setChatPrefill(null);
        driverObj.destroy();
      },
      onDestroyed: () => {
        cleanup();
      },
    });

    driverRef.current = driverObj;
    driverObj.drive();
  }, [handleStepChange, cleanup, setChatPrefill, setCalendarView, t]);

  useEffect(() => {
    if (isWalkthroughComplete()) return;
    if (shouldMobileInstallDialogOpen) return; // Skip walkthrough if mobile install dialog is open

    const timer = setTimeout(() => startWalkthrough(), 3000);

    return () => {
      clearTimeout(timer);
      cleanup();
      driverRef.current?.destroy();
    };
  }, [startWalkthrough, cleanup, shouldMobileInstallDialogOpen]);

  useEffect(() => {
    const handleTaskCreated = () => {
      if (!driverRef.current) return;
      if (driverRef.current.getState().activeIndex === 1) {
        driverRef.current.moveNext();
      }
    };
    window.addEventListener('task:created', handleTaskCreated);
    return () => window.removeEventListener('task:created', handleTaskCreated);
  }, []);

  useEffect(() => {
    const handleAiMessageSent = () => {
      if (!driverRef.current) return;
      if (driverRef.current.getState().activeIndex === 3) {
        markWalkthroughComplete();
        setChatPrefill(null);
        driverRef.current.destroy();
      }
    };
    window.addEventListener('ai-chat:message-sent', handleAiMessageSent);
    return () => window.removeEventListener('ai-chat:message-sent', handleAiMessageSent);
  }, [setChatPrefill]);

  return null;
};
